<template>
  <div
    class="min-h-screen bg-gradient-to-br from-[#27254A] via-[#2D2B5A] to-[#27254A]"
  >
    <!-- Background Pattern -->
    <div class="fixed inset-0 opacity-10">
      <div
        class="absolute inset-0"
        style="
          background-image: url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23E472E1\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E');
        "
      ></div>
    </div>

    <div class="relative z-10 flex flex-col min-h-screen">
      <div class="flex-grow p-6">
        <div class="max-w-6xl mx-auto">
          <!-- Welcome Section -->
          <div class="text-center mb-16 py-8">
            <div
              class="inline-flex items-center justify-center w-24 h-24 bg-black/30 backdrop-blur-sm rounded-3xl mb-6 border border-white/10"
            >
              <img
                src="/favicon.svg"
                alt="BioCorteX"
                class="w-20 h-20 object-contain"
              />
            </div>
            <h1 class="text-5xl font-bold text-white mb-4">
              Welcome to BioCorteX
            </h1>
            <p class="text-2xl text-gray-300 mb-4">
              Carbon Mirror performs 'in-silico bridge' trials, delivering
              credibility weighting across science, regulatory and commercial
              divisions, increasing POS and unlocking value
            </p>

            <p class="text-xl text-[#E472E1] font-semibold mb-8">
              70 billion nodes and relations
            </p>

            <!-- Proceed to App Button -->
            <button
              class="inline-flex items-center gap-3 px-8 py-4 bg-[#e055dc] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
              @click="proceedToApp"
            >
              <span>Launch Application</span>
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                ></path>
              </svg>
            </button>
          </div>

          <!-- Platform Features Section -->
          <div class="mb-12">
            <h2 class="text-3xl font-bold text-white mb-10 text-center">
              Run '<i class="text-3xl font-bold text-white">in-silico</i>
              Bridge' Trials on the BioCorteX Platform
            </h2>

            <div class="grid md:grid-cols-3 gap-6">
              <!-- Feature 1 -->
              <div
                class="bg-black/30 backdrop-blur-md rounded-2xl border border-white/10 p-6 hover:bg-black/40 hover:border-white/20 transition-all duration-300 hover:shadow-xl hover:shadow-[#E472E1]/20 transform hover:-translate-y-1"
              >
                <div class="text-center">
                  <div
                    class="w-20 h-20 bg-[#E472E1]/20 rounded-full flex items-center justify-center mx-auto mb-4"
                  >
                    <svg
                      class="w-10 h-10 text-[#E472E1]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                      ></path>
                    </svg>
                  </div>
                  <h3 class="text-xl font-semibold text-white mb-2">
                    No black box
                  </h3>
                  <p class="text-gray-300 text-sm">
                    Fully mechanistic approach, grounded in scientific data
                  </p>
                </div>
              </div>

              <!-- Feature 2 -->
              <div
                class="bg-black/30 backdrop-blur-md rounded-2xl border border-white/10 p-6 hover:bg-black/40 hover:border-white/20 transition-all duration-300 hover:shadow-xl hover:shadow-[#E472E1]/20 transform hover:-translate-y-1"
              >
                <div class="text-center">
                  <div
                    class="w-20 h-20 bg-[#E472E1]/20 rounded-full flex items-center justify-center mx-auto mb-4"
                  >
                    <svg
                      class="w-10 h-10 text-[#E472E1]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      ></path>
                    </svg>
                  </div>
                  <h3 class="text-xl font-semibold text-white mb-2">
                    No hallucinations
                  </h3>
                  <p class="text-gray-300 text-sm">
                    Our AI is only used to help traverse our large graph and run
                    the trial with the details your provide
                  </p>
                </div>
              </div>

              <!-- Feature 3 -->
              <div
                class="bg-black/30 backdrop-blur-md rounded-2xl border border-white/10 p-6 hover:bg-black/40 hover:border-white/20 transition-all duration-300 hover:shadow-xl hover:shadow-[#E472E1]/20 transform hover:-translate-y-1"
              >
                <div class="text-center">
                  <div
                    class="w-20 h-20 bg-[#E472E1]/20 rounded-full flex items-center justify-center mx-auto mb-4"
                  >
                    <svg
                      class="w-10 h-10 text-[#E472E1]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      ></path>
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      ></path>
                    </svg>
                  </div>
                  <h3 class="text-xl font-semibold text-white mb-2">
                    Full transparency
                  </h3>
                  <p class="text-gray-300 text-sm">
                    Our graph shows the full details and the exact methodology,
                    allowing you to inspect and change any aspect of the trial
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Resources & Documentation Section -->
          <div class="mb-12">
            <h2 class="text-3xl font-bold text-white mb-8 text-center">
              Resources & Documentation
            </h2>

            <div class="space-y-6">
              <!-- Getting Started -->
              <div class="border border-white/10 rounded-lg overflow-hidden">
                <button
                  class="w-full p-4 bg-white/5 hover:bg-white/10 transition-colors duration-200 flex items-center justify-between text-left"
                  @click="activeAccordion = activeAccordion === 0 ? null : 0"
                >
                  <span class="text-white font-semibold">Getting Started</span>
                  <svg
                    class="w-5 h-5 text-[#E472E1] transition-transform duration-200"
                    :class="{ 'rotate-180': activeAccordion === 0 }"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 9l6 6 6-6"
                    ></path>
                  </svg>
                </button>

                <div
                  v-show="activeAccordion === 0"
                  class="border-t border-white/10 p-6 bg-black/20 animate-in"
                >
                  <h3 class="text-xl font-semibold text-white mb-6">
                    Getting Started Guide
                  </h3>

                  <div class="space-y-8">
                    <!-- Step 1 -->
                    <div class="flex flex-col gap-4">
                      <div class="text-center">
                        <h4 class="text-lg font-semibold text-white mb-2">
                          1. Traverse the Knowledge Graph
                        </h4>
                      </div>
                      <div class="w-full">
                        <img
                          src="@/assets/getting_started/1-traverse-graph.png"
                          alt="Traverse the knowledge graph"
                          class="w-full max-w-md mx-auto rounded-lg shadow-lg border border-white/10"
                        />
                      </div>
                      <div class="text-center">
                        <p class="text-gray-300">
                          Navigate through the interconnected biological data to
                          explore relationships between genes, proteins,
                          diseases, and treatments.
                        </p>
                      </div>
                    </div>

                    <!-- Step 2 -->
                    <div class="flex flex-col gap-4">
                      <div class="text-center">
                        <h4 class="text-lg font-semibold text-white mb-2">
                          2. Set Columns of Interest and Filters
                        </h4>
                      </div>
                      <div class="w-full">
                        <img
                          src="@/assets/getting_started/2-set-cols-of-iterest-and-filters.png"
                          alt="Set columns of interest and filters"
                          class="w-full max-w-md mx-auto rounded-lg shadow-lg border border-white/10"
                        />
                      </div>
                      <div class="text-center">
                        <p class="text-gray-300">
                          Customize your view by selecting specific data types,
                          organisms, or biological pathways to focus on.
                        </p>
                      </div>
                    </div>

                    <!-- Step 3 -->
                    <div class="flex flex-col gap-4">
                      <div class="text-center">
                        <h4 class="text-lg font-semibold text-white mb-2">
                          3. View Results
                        </h4>
                      </div>
                      <div class="w-full">
                        <img
                          src="@/assets/getting_started/3-view-results.png"
                          alt="View query results"
                          class="w-full max-w-md mx-auto rounded-lg shadow-lg border border-white/10"
                        />
                      </div>
                      <div class="text-center">
                        <p class="text-gray-300">
                          Analyze your query results with interactive
                          visualizations and detailed data tables showing
                          relationships and patterns.
                        </p>
                      </div>
                    </div>

                    <!-- Step 4 -->
                    <div class="flex flex-col gap-4">
                      <div class="text-center">
                        <h4 class="text-lg font-semibold text-white mb-2">
                          4. Plot and Analyze Results
                        </h4>
                      </div>
                      <div class="w-full">
                        <img
                          src="@/assets/getting_started/4-plot-results.png"
                          alt="Plot and analyze results"
                          class="w-full max-w-md mx-auto rounded-lg shadow-lg border border-white/10"
                        />
                      </div>
                      <div class="text-center">
                        <p class="text-gray-300">
                          Create custom plots and perform advanced analysis on
                          your selected data to uncover insights and trends.
                        </p>
                      </div>
                    </div>

                    <!-- Step 5 -->
                    <div class="flex flex-col gap-4">
                      <div class="text-center">
                        <h4 class="text-lg font-semibold text-white mb-2">
                          5. Use AI Agent for Assistance
                        </h4>
                      </div>
                      <div class="w-full">
                        <img
                          src="@/assets/getting_started/5-acheive-all-this-and-more-through-agent.png"
                          alt="Use AI agent for assistance"
                          class="w-full max-w-md mx-auto rounded-lg shadow-lg border border-white/10"
                        />
                      </div>
                      <div class="text-center">
                        <p class="text-gray-300">
                          Leverage our AI-powered agent to get personalized
                          recommendations, ask questions, and discover
                          additional insights from your data.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Case Study 1: Summit Therapeutics -->
              <div class="border border-white/10 rounded-lg overflow-hidden">
                <button
                  class="w-full p-4 bg-white/5 hover:bg-white/10 transition-colors duration-200 flex items-center justify-between text-left"
                  @click="activeAccordion = activeAccordion === 1 ? null : 1"
                >
                  <span class="text-white font-semibold"
                    >Case Study 1: Summit Therapeutics</span
                  >
                  <svg
                    class="w-5 h-5 text-[#E472E1] transition-transform duration-200"
                    :class="{ 'rotate-180': activeAccordion === 1 }"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 9l6 6 6-6"
                    ></path>
                  </svg>
                </button>

                <div
                  v-show="activeAccordion === 1"
                  class="border-t border-white/10 p-6 bg-black/20 animate-in"
                >
                  <h3 class="text-xl font-semibold text-white mb-4">
                    Predicting Success: BioCorteX Analysis of Summit
                    Therapeutics
                  </h3>

                  <div class="space-y-6">
                    <div>
                      <h4 class="font-semibold text-white mb-3">
                        The Asset: Ivonescimab (AK112)
                      </h4>
                      <p class="text-gray-300 mb-3">
                        Summit Therapeutics made waves by licensing Ivonescimab,
                        a first-in-class bispecific antibody from Akeso (China).
                        This asset targets both PD-1 and VEGF, aiming to
                        revolutionise lung cancer treatment.
                      </p>
                    </div>

                    <div>
                      <h4 class="font-semibold text-white mb-3">
                        Market Impact
                      </h4>
                      <p class="text-gray-300 mb-3">
                        Summit's valuation has seen dramatic shifts tied to
                        clinical data releases. Most notably, their share price
                        surged over 200% following data suggesting Ivonescimab
                        outperformed established blockbusters in Phase III
                        trials. However,the market remains hyper-sensitive to
                        the nuances of TME (Tumour Microenvironment)
                        interactions and long-term safety.
                      </p>
                    </div>

                    <div>
                      <h4 class="font-semibold text-white mb-3">
                        Our Analysis: Beyond Surface-Level Data
                      </h4>
                      <p class="text-gray-300 mb-3">
                        BioCorteX didn't just look at the headlines. By
                        deploying Carbon Knowledge, and Carbon Mirror platforms
                        to assess the likelihood of success for Ivonescimab.
                      </p>
                      <p class="text-gray-300 mb-3">
                        By running hundreds of thousands of In-Silico
                        simulations that mimic the trials structure, derived
                        from the inclusion/exlusion criteria, indication
                        selection, and possible geographies/centers for running
                        this trial; BioCorteX was able to calculate the levels
                        of interaction between the asset and the TME.
                      </p>
                    </div>

                    <div
                      class="p-4 bg-[#E472E1]/10 rounded-lg border border-[#E472E1]/30"
                    >
                      <h4 class="font-semibold text-white mb-3">The Result</h4>
                      <p class="text-gray-200 mb-3">
                        A probabilistic roadmap of trial success that accounts
                        for biological complexity that traditional statistical
                        models often miss.
                      </p>
                      <p class="text-gray-200">
                        The initial analysis showed that inter tumour bacteria
                        produce proteins that directly interfere with
                        Ivonescimab [mechanisms are proprietary to BioCorteX].
                      </p>
                      <div class="mt-6">
                        <img
                          src="@/assets/home/ivonescimab-interacting-bacteria.png"
                          alt="Ivonescimab Interacting Bacteria Analysis"
                          class="w-full max-w-2xl mx-auto rounded-lg shadow-lg bg-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Case Study 2: GLP-1 Asset Analysis -->
              <div class="border border-white/10 rounded-lg overflow-hidden">
                <button
                  class="w-full p-4 bg-white/5 hover:bg-white/10 transition-colors duration-200 flex items-center justify-between text-left"
                  @click="activeAccordion = activeAccordion === 2 ? null : 2"
                >
                  <span class="text-white font-semibold"
                    >Case Study 2: GLP-1 Asset Analysis</span
                  >
                  <svg
                    class="w-5 h-5 text-[#E472E1] transition-transform duration-200"
                    :class="{ 'rotate-180': activeAccordion === 2 }"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 9l6 6 6-6"
                    ></path>
                  </svg>
                </button>

                <div
                  v-show="activeAccordion === 2"
                  class="border-t border-white/10 p-6 bg-black/20 animate-in"
                >
                  <h3 class="text-xl font-semibold text-white mb-4">
                    Decoding the GLP-1 Revolution: In-Silico Asset Analysis
                  </h3>

                  <div class="space-y-6">
                    <div>
                      <h4 class="font-semibold text-white mb-3">
                        The Market: A $180B Opportunity
                      </h4>
                      <p class="text-gray-300 mb-3">
                        GLP-1 receptor agonists have evolved from diabetes
                        treatments into a global health phenomenon. By mimicking
                        natural hormones to regulate insulin and appetite, these
                        assets are the new gold standard for Obesity,
                        Cardiovascular health, and MASH.
                      </p>
                      <p class="text-gray-300 mb-3">
                        Market Impact: Currently valued at $52B, the sector is
                        projected to hit $180B by 2032. With hundreds of
                        candidates in the pipeline, the ability to distinguish
                        between "me-too" drugs and true breakthroughs is the
                        primary challenge for developers and investors.
                      </p>
                    </div>

                    <div>
                      <h4 class="font-semibold text-white mb-3">
                        Our Analysis: Precision Metabolic Modeling
                      </h4>
                      <p class="text-gray-300 mb-3">
                        BioCorteX utilises Carbon Knowledge and Carbon Mirror to
                        look beyond surface-level weight loss metrics. We
                        analyse the systemic biological impact of new GLP-1
                        candidates through massive-scale simulation.
                      </p>
                      <p class="text-gray-300 font-semibold mb-2">
                        In-Silico Trial Emulation:
                      </p>
                      <p class="text-gray-300 mb-3 ml-4">
                        We run hundreds of thousands of simulations mirroring
                        exact trial structures, including specific
                        inclusion/exclusion criteria and geographic demographic
                        variations.
                      </p>
                      <p class="text-gray-300 font-semibold mb-2">
                        Systemic Interaction:
                      </p>
                      <p class="text-gray-300 mb-3 ml-4">
                        Our platforms calculate the interplay between the asset
                        and the metabolic environment, predicting efficacy in
                        gastric emptying, receptor sensitivity, and glucose
                        regulation.
                      </p>
                      <p class="text-gray-300 font-semibold mb-2">
                        Tolerability Forecasting:
                      </p>
                      <p class="text-gray-300 mb-3 ml-4">
                        We simulate gastrointestinal response across virtual
                        cohorts to predict real-world adherence and "drop-out"
                        rates—critical factors in long-term trial success.
                      </p>
                    </div>

                    <div
                      class="p-4 bg-[#E472E1]/10 rounded-lg border border-[#E472E1]/30"
                    >
                      <h4 class="font-semibold text-white mb-3">
                        The Result: High-Fidelity Probabilities
                      </h4>
                      <p class="text-gray-200 mb-3">
                        The result is a probabilistic roadmap that accounts for
                        the metabolic complexity traditional models miss.
                        BioCorteX identifies assets with the highest likelihood
                        of clinical superiority and commercial staying power.
                      </p>
                      <p class="text-gray-200">
                        The probabilistic outcome is shown as a figure per MoA
                        across all trials. An example illustrative figure shown
                        below. The figure shows the number of trial simulations
                        on the y-axis. The x-axis shows the change in
                        interaction score per simulation relative to a baseline
                        trial, e.g. GLORY-1 baseline.
                      </p>
                      <div class="mt-6">
                        <img
                          src="@/assets/home/bacterial-trial-interference.png"
                          alt="Bacterial Trial Interference Analysis"
                          class="w-full max-w-2xl mx-auto rounded-lg shadow-lg border border-white/10"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Contact us -->
              <div class="border border-white/10 rounded-lg overflow-hidden">
                <button
                  class="w-full p-4 bg-white/5 hover:bg-white/10 transition-colors duration-200 flex items-center justify-between text-left"
                  @click="activeAccordion = activeAccordion === 3 ? null : 3"
                >
                  <span class="text-white font-semibold">Contact us</span>
                  <svg
                    class="w-5 h-5 text-[#E472E1] transition-transform duration-200"
                    :class="{ 'rotate-180': activeAccordion === 3 }"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 9l6 6 6-6"
                    ></path>
                  </svg>
                </button>

                <div
                  v-show="activeAccordion === 3"
                  class="border-t border-white/10 p-6 bg-black/20 animate-in"
                >
                  <h3 class="text-xl font-semibold text-white mb-4">
                    Get in Touch
                  </h3>
                  <p class="mb-6 text-gray-300">
                    Interested in learning more about BioCorteX or need
                    assistance with our platform? We're here to help.
                  </p>
                  <div class="space-y-4">
                    <div
                      class="flex items-center gap-4 p-4 bg-white/5 rounded-lg"
                    >
                      <div
                        class="w-12 h-12 min-w-12 bg-[#E472E1]/20 rounded-full flex items-center justify-center"
                      >
                        <svg
                          class="w-6 h-6 text-[#E472E1]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          ></path>
                        </svg>
                      </div>
                      <div>
                        <h4 class="font-semibold text-white">Sales</h4>
                        <p class="text-sm text-gray-300">
                          Learn more about our platform and pricing options
                        </p>
                      </div>
                    </div>

                    <div
                      class="flex items-center gap-4 p-4 bg-white/5 rounded-lg"
                    >
                      <div
                        class="w-12 h-12 min-w-12 bg-[#E472E1]/20 rounded-full flex items-center justify-center"
                      >
                        <svg
                          class="w-6 h-6 text-[#E472E1]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                          ></path>
                        </svg>
                      </div>
                      <div>
                        <h4 class="font-semibold text-white">Support</h4>
                        <p class="text-sm text-gray-300">
                          Get technical help and platform assistance
                        </p>
                      </div>
                    </div>

                    <div
                      class="p-4 bg-[#E472E1]/10 rounded-lg border border-[#E472E1]/30 text-center"
                    >
                      <a
                        href="mailto:info@biocortex.com"
                        class="inline-flex items-center gap-2 text-[#E472E1] hover:text-white transition-colors duration-200"
                      >
                        <svg
                          class="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          ></path>
                        </svg>
                        <span class="font-mono text-lg"
                          >info@biocortex.com</span
                        >
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();
const activeAccordion = ref(null);

function proceedToApp() {
  router.push("/graph");
}
</script>

<style scoped>
/* Force dark theme for PrimeVue tabs */
:deep(.p-tabs) {
  --p-tabs-tablist-background: transparent;
  --p-tabs-tablist-border-color: #4b5563;
  --p-tabs-tab-background: transparent;
  --p-tabs-tab-hover-background: transparent;
  --p-tabs-tab-active-background: transparent;
  --p-tabs-tab-color: #d1d5db;
  --p-tabs-tab-hover-color: #f9fafb;
  --p-tabs-tab-active-color: #e472e1;
  --p-tabs-tab-active-border-color: #e472e1;
  --p-tabs-tab-focus-ring-color: #e472e1;
  --p-content-background: transparent;
}

:deep(.p-tablist) {
  background: transparent !important;
  border-bottom: 1px solid #4b5563 !important;
}

:deep(.p-tab) {
  background: transparent !important;
  color: #d1d5db !important;
  border: none !important;
}

:deep(.p-tab:hover) {
  background: transparent !important;
  color: #f9fafb !important;
}

:deep(.p-tab.p-tab-active) {
  background: transparent !important;
  color: #e472e1 !important;
  border-bottom: 2px solid #e472e1 !important;
}

:deep(.p-tabpanel) {
  background: transparent !important;
  color: inherit !important;
  border: none !important;
  padding: 0 !important;
}

/* Custom accordion animations */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-in {
  animation: slideDown 0.3s ease-out;
}

.rotate-180 {
  transform: rotate(180deg);
}
</style>
